import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

export const PORTAL_CUSTOMER_ID_KEY = 'portal_customer_id';
export const PORTAL_STORE_ID_KEY = 'portal_store_id';

type MonitoringValue = string | number | boolean | null | undefined;
export type MonitoringProperties = Record<string, MonitoringValue>;

interface PortalIdentity {
  customerId?: string | null;
  storeId?: string | null;
}

const getPortalDomain = () =>
  typeof window !== 'undefined' ? window.location.hostname : 'unknown';

const cleanProperties = (properties: MonitoringProperties): MonitoringProperties =>
  Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined && value !== ''),
  );

export const getStoredPortalIdentity = (): PortalIdentity => {
  if (typeof window === 'undefined') return {};

  return {
    customerId: localStorage.getItem(PORTAL_CUSTOMER_ID_KEY),
    storeId: localStorage.getItem(PORTAL_STORE_ID_KEY),
  };
};

export const getBasePortalProperties = (
  properties: MonitoringProperties = {},
): MonitoringProperties =>
  cleanProperties({
    platform: 'lasso_customer_portal',
    portal_domain: getPortalDomain(),
    ...properties,
  });

export const getMonitoringErrorCategory = (error: unknown): string => {
  const err = error as {
    response?: { status?: number };
    monitoringCategory?: string;
    code?: string;
    name?: string;
    message?: string;
  };

  if (err?.monitoringCategory) return err.monitoringCategory;
  if (err?.response?.status) return `http_${err.response.status}`;
  if (err?.code) return err.code;
  if (err?.name) return err.name;
  if (err?.message) return 'unknown_error';

  return 'unknown';
};

export const registerPortalContext = (properties: MonitoringProperties = {}) => {
  const portalProperties = getBasePortalProperties(properties);

  try {
    posthog.register(portalProperties);
  } catch (_) {
    // Monitoring must never block the customer portal experience.
  }

  Sentry.setTags(
    Object.fromEntries(
      Object.entries(portalProperties)
        .filter(([, value]) => typeof value === 'string' || typeof value === 'number')
        .map(([key, value]) => [key, String(value)]),
    ),
  );
};

export const identifyPortalSession = ({ customerId, storeId }: PortalIdentity) => {
  if (typeof window !== 'undefined') {
    if (customerId) localStorage.setItem(PORTAL_CUSTOMER_ID_KEY, customerId);
    if (storeId) localStorage.setItem(PORTAL_STORE_ID_KEY, storeId);
  }

  registerPortalContext({
    customer_id: customerId,
    store_id: storeId,
  });

  if (customerId) {
    try {
      posthog.identify(
        customerId,
        getBasePortalProperties({
          store_id: storeId,
        }),
      );
    } catch (_) {
      // Monitoring must never block the customer portal experience.
    }

    Sentry.setUser({ id: customerId });
  }
};

export const clearPortalMonitoringIdentity = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(PORTAL_CUSTOMER_ID_KEY);
    localStorage.removeItem(PORTAL_STORE_ID_KEY);
  }

  try {
    posthog.reset();
  } catch (_) {
    // Monitoring must never block the customer portal experience.
  }

  Sentry.setUser(null);
};

export const capturePortalEvent = (eventName: string, properties: MonitoringProperties = {}) => {
  try {
    posthog.capture(eventName, getBasePortalProperties(properties));
  } catch (_) {
    // Monitoring must never block the customer portal experience.
  }
};

export const capturePortalLogout = () => {
  const { customerId, storeId } = getStoredPortalIdentity();

  capturePortalEvent('portal_logout', {
    customer_id: customerId,
    store_id: storeId,
  });
  clearPortalMonitoringIdentity();
};

export const captureApiException = (error: unknown, clientName: 'public' | 'portal') => {
  const err = error as {
    response?: { status?: number; data?: unknown };
    config?: { url?: string; method?: string };
    message?: string;
  };
  const status = err?.response?.status;
  const skipStatuses = [401, 403, 404];

  if (skipStatuses.includes(status ?? 0) || err?.message?.includes('canceled')) {
    return;
  }

  Sentry.captureException(error, {
    tags: {
      context: 'api_request',
      client: clientName,
      http_status: String(status || 'network_error'),
      api_path: err?.config?.url || 'unknown',
    },
    extra: {
      method: err?.config?.method?.toUpperCase() || 'UNKNOWN',
      status,
      url: err?.config?.url,
      responseData: err?.response?.data,
    },
  });
};
